import { createElement, FC, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router'
import { cn } from '@/lib/utils'
import { NavigationItem } from './types'

interface AnimatedSidebarProps {
  items: NavigationItem[]
  className?: string
}

const transition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.15,
}

const getHoverAnimationProps = (hoveredRect: DOMRect, navRect: DOMRect) => ({
  x: 18,
  y: hoveredRect.top - navRect.top - 8,
  width: navRect.width - 36,
  height: hoveredRect.height + 16,
})

export const AnimatedSidebar: FC<AnimatedSidebarProps> = ({ items, className }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [buttonRefs, setButtonRefs] = useState<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    setButtonRefs(prev => prev.slice(0, items.length))
  }, [items.length])

  const navRef = useRef<HTMLDivElement>(null)
  const navRect = navRef.current?.getBoundingClientRect()

  const selectedIndex = useMemo(() => {
    // Find the most specific match
    // Sort by path length (longer paths first) to ensure most specific match
    const sortedItems = [...items].sort((a, b) => {
      const aLength = a.path.length
      const bLength = b.path.length
      return bLength - aLength
    })

    const index = sortedItems.findIndex(item => pathname.startsWith(item.path))

    // If found, convert back to original index
    if (index !== -1) {
      const matchedItem = sortedItems[index]
      return items.findIndex(item => item.path === matchedItem.path)
    }

    return -1
  }, [items, pathname])

  const selectedRect = selectedIndex !== -1 ? buttonRefs[selectedIndex]?.getBoundingClientRect() : undefined

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const hoveredRect = buttonRefs[hoveredIndex ?? -1]?.getBoundingClientRect()

  return (
    <div className={cn('flex flex-col h-full min-h-0 border-r bg-background w-60 rounded-l-xl', className)}>
      <nav ref={navRef} className="relative z-0 flex flex-col gap-2 p-4 overflow-y-auto">
        {items.map((item, i) => {
          const isActive = selectedIndex === i

          return (
            <button
              key={item.path}
              className="relative z-20 flex items-center px-3 py-2 text-sm transition-colors bg-transparent rounded-md cursor-pointer select-none group"
              onPointerEnter={() => setHoveredIndex(i)}
              onPointerLeave={() => setHoveredIndex(null)}
              onFocus={() => setHoveredIndex(i)}
              onClick={() => navigate(item.path)}
              type="button"
              data-testid={`inner-tab-${item.title.toLowerCase().replace(/\s+/g, '')}`}
            >
              <motion.span
                ref={el => {
                  buttonRefs[i] = el as HTMLButtonElement
                }}
                className={cn('flex items-center transition-colors duration-300 group-hover:text-foreground', {
                  'text-muted-foreground': !isActive,
                  'text-foreground': isActive,
                })}
              >
                {item.icon &&
                  createElement(item.icon, {
                    className: cn(
                      'mr-2 h-4 w-4 group-hover:text-foreground',
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    ),
                  })}
                {item.title}
              </motion.span>
            </button>
          )
        })}

        {/* Hover animation */}
        <AnimatePresence>
          {hoveredRect && navRect && (
            <motion.div
              key="hover"
              className="absolute top-0 left-0 z-10 p-0 m-0 rounded-md bg-muted/60"
              initial={{ ...getHoverAnimationProps(hoveredRect, navRect), opacity: 0 }}
              animate={{ ...getHoverAnimationProps(hoveredRect, navRect), opacity: 1 }}
              exit={{ ...getHoverAnimationProps(hoveredRect, navRect), opacity: 0 }}
              transition={transition}
            />
          )}
        </AnimatePresence>

        {/* Selected item indicator */}
        {selectedRect && navRect && selectedIndex !== -1 && (
          <motion.div
            className="absolute left-0 z-10 rounded-md bg-muted"
            initial={false}
            animate={{
              width: navRect.width - 36,
              height: selectedRect.height + 16,
              x: 18,
              y: selectedRect.top - navRect.top - 24,
              opacity: 1,
            }}
            transition={transition}
          />
        )}
      </nav>
    </div>
  )
}
